import {useDispatch, useSelector} from 'react-redux';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';

import {usePlugState, PlugState, UserTariffCountKey} from 'entities/user';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';
import IPagination from 'shared/api/model/IPagination';
import {TypedDispatch} from 'shared/model/types';

import {SelectionSelector} from './createSelectionSlice';
import useMassOperationsMode from './useMassOperationsMode';

export interface IMonitorListSelection {
    selectedIdsSelector: SelectionSelector<string[]>,
    isSelectedAllSelector: SelectionSelector<boolean>,
    selectAllOnPage: ActionCreatorWithPayload<string[]>,
    clearAll: ActionCreatorWithPayload<number>,
}

interface IUseMonitorListParams {
    pagination: IPagination<{uuid: string}> | undefined,
    isLoading: boolean,
    tariffField: UserTariffCountKey,
    hasFilters: boolean,
    selection: IMonitorListSelection,
    getAllIds?: () => Promise<string[] | undefined>,
}

export interface IMonitorListState {
    plugState: PlugState,
    isRedirecting: boolean,
    selectedCount: number,
    hasSelectedItems: boolean,
    isSelectAllChecked: boolean,
    isMassOperationsMode: boolean,
    toggleMassOperationsMode: () => void,
    exitMassOperationsMode: () => void,
    handleSelectAll: (_checked: boolean) => void,
    clearSelection: () => void,
}

export const useMonitorList = ({
    pagination,
    isLoading,
    tariffField,
    hasFilters,
    selection,
    getAllIds,
}: IUseMonitorListParams): IMonitorListState => {
    const {selectedIdsSelector, isSelectedAllSelector, selectAllOnPage, clearAll} = selection;
    const dispatch = useDispatch<TypedDispatch>();
    const selectedIds = useSelector(selectedIdsSelector);
    const isSelectedAll = useSelector(isSelectedAllSelector);
    const totalItems = pagination?.totalItems || 0;

    const isRedirecting = useResetPageOnEmpty(pagination);

    const {isMassOperationsMode, toggleMassOperationsMode, exitMassOperationsMode} = useMassOperationsMode(
        () => dispatch(clearAll(totalItems)),
    );

    const plugState = usePlugState({
        tariffField,
        isLoading: isLoading || isRedirecting,
        isEmpty: pagination?.items?.length === 0 && pagination?.totalItems === 0,
        hasFilters,
    });

    const hasSelectedItems = selectedIds.length > 0;

    const handleSelectAll = async(checked: boolean) => {
        if (!checked) {
            dispatch(clearAll(totalItems));
            return;
        }

        const ids = getAllIds
            ? await getAllIds()
            : (pagination?.items ?? []).map(item => item.uuid);

        if (ids) {
            dispatch(selectAllOnPage(ids));
        }
    };

    return {
        plugState,
        isRedirecting,
        selectedCount: selectedIds.length,
        hasSelectedItems,
        isSelectAllChecked: (!isLoading && pagination ? isSelectedAll : false) && hasSelectedItems,
        isMassOperationsMode,
        toggleMassOperationsMode,
        exitMassOperationsMode,
        handleSelectAll,
        clearSelection: () => dispatch(clearAll(0)),
    };
};

export default useMonitorList;
